import { useState, type ReactNode } from "react";
import { useQuoteCopy } from "../i18n/QuoteCopyContext";
import type { FormState, Outcome } from "../types";
import { ContactCard } from "./ContactCard";
import { EmailPreview } from "./EmailPreview";
import { CheckIcon, CopyIcon } from "./icons";

interface Props {
  form: FormState;
  outcome: Outcome;
  contactName: string;
  sentSubject: string;
  sentBody: string;
  onStartOver: () => void;
}

function EmailPlaceholder({ email }: { email: string }): ReactNode {
  const copy = useQuoteCopy();
  const marker = "{email}";
  const template = copy.t("result.teamWillContactEmail");
  const index = template.indexOf(marker);
  if (index === -1) return copy.t("result.teamWillContactEmail", { email });
  return (
    <>
      {template.slice(0, index)}
      <strong>{email}</strong>
      {template.slice(index + marker.length)}
    </>
  );
}

export function ResultSuccess({
  form,
  outcome,
  contactName,
  sentSubject,
  sentBody,
  onStartOver,
}: Props) {
  const copy = useQuoteCopy();
  const [copied, setCopied] = useState(false);

  const copyRef = async () => {
    if (!outcome.refId) return;
    try {
      await navigator.clipboard.writeText(outcome.refId);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      // Clipboard unavailable (e.g. insecure context).
    }
  };

  return (
    <section className="card" aria-labelledby="nv-result-title">
      <div className="nv-card-scroll">
        <div className="nv-result-head">
          <span className="nv-result-icon success" aria-hidden="true">
            <CheckIcon size={26} />
          </span>
          <div>
            <h2 className="nv-result-title" id="nv-result-title" data-step-heading tabIndex={-1}>
              {copy.t("result.thankYou")}
            </h2>
            <p className="nv-result-sub" role="status">
              {form.email ? <EmailPlaceholder email={form.email} /> : copy.t("result.teamWillContact")}
            </p>
          </div>
        </div>

        <div className="nv-result-body">
          {outcome.demo && (
            <div className="nv-result-note">
              {copy.t("result.previewMode")}
              {outcome.demoDroppedFiles ? copy.t("result.previewFiles") : null}
            </div>
          )}

          <div className="nv-result-grid">
            <div>
              {outcome.refId && (
                <div className="nv-ref">
                  <div className="nv-ref-info">
                    <span className="nv-ref-label">{copy.t("result.yourReference")}</span>
                    <span className="nv-ref-value">{outcome.refId}</span>
                  </div>
                  <button type="button" className="nv-ref-copy" onClick={copyRef}>
                    {copied ? <CheckIcon size={15} /> : <CopyIcon size={15} />}
                    {copied ? copy.t("result.copied") : copy.t("result.copy")}
                  </button>
                </div>
              )}
              <ContactCard title={contactName} />
            </div>
            <div>
              <h3 className="nv-next-title">{copy.t("result.nextTitle")}</h3>
              <ol className="nv-next-list">
                {(["result.next1", "result.next2", "result.next3"] as const).map((key, i) => (
                  <li key={key}>
                    <span className="nv-next-num">{i + 1}</span>
                    <span>{copy.t(key)}</span>
                  </li>
                ))}
              </ol>
            </div>
          </div>

          {outcome.emailSent === false && !outcome.demo && (
            <p className="nv-result-soft">{copy.t("result.logged")}</p>
          )}

          <EmailPreview subject={sentSubject} bodyHtml={sentBody} />
        </div>
      </div>

      <div className="confirm-actions end">
        <button type="button" className="btn-submit" onClick={onStartOver}>
          {copy.t("result.newQuote")}
        </button>
      </div>
    </section>
  );
}
